/* Treemap helpers shared by the live Market Map (mobile src/v2/v3/Markets.jsx
   + desktop src/v3desktop/pages/Markets.jsx). Extracted 2026-08-15 from
   src/v2/components/FinvizMap.jsx when its never-rendered component body
   (the pre-V3 Discover map) was deleted in the audit cleanup — these three
   exports were the only live code in that 539-line file. Pure functions +
   config, no React. */

export const RANGE_CONFIG = {
  '1D': { field: 'change1d',         bound: 3,   label: '1D' },
  '5D': { field: 'price_5d_change',  bound: 5,   label: '5D' },
  '1M': { field: 'price_1m_change',  bound: 10,  label: '1M' },
  '6M': { field: 'price_6m_change',  bound: 25,  label: '6M' },
  '1Y': { field: 'price_1y_change',  bound: 50,  label: '1Y' },
  '5Y': { field: 'price_5y_change',  bound: 150, label: '5Y' },
}

export function colorForPct(pct, bound = 3) {
  // Ramp brightness with magnitude in both directions, scaled to a per-range
  // bound. The same 7-step ramp at ±3 (1D) maps to ±50 for 1Y so the color
  // distribution stays useful across timeframes.
  if (pct == null || !isFinite(pct)) return '#3a3d44'
  const norm = pct / bound // -1..+1 at the extremes, can exceed beyond
  if (norm <= -1)   return '#7a1019'
  if (norm <= -0.66) return '#9d1622'
  if (norm <= -0.33) return '#c01c2a'
  if (norm <  0)    return '#8b3a40'
  if (norm === 0)   return '#404244'
  if (norm <  0.33) return '#2d6a3f'
  if (norm <  0.66) return '#1ea34a'
  if (norm <  1)    return '#14b552'
  return              '#00c853'
}

function worst(rowAreas, w) {
  let max = -Infinity, min = Infinity, sum = 0
  for (const a of rowAreas) {
    sum += a
    if (a > max) max = a
    if (a < min) min = a
  }
  if (sum <= 0 || w <= 0) return Infinity
  const s2 = sum * sum
  const w2 = w * w
  return Math.max((w2 * max) / s2, s2 / (w2 * min))
}

// In-place squarify: items = sorted desc by .area. rect = {x,y,w,h}.
// Returns array of items with {x,y,w,h} assigned.
// Exported for reuse by the v3 Market Map — pure function, no behavior change.
export function squarify(items, rect) {
  const out = []
  let remaining = items.slice()
  let current = { ...rect }

  while (remaining.length > 0) {
    if (current.w <= 0 || current.h <= 0) break
    const shortSide = Math.min(current.w, current.h)
    let row = []
    let bestWorst = Infinity

    for (let i = 0; i < remaining.length; i++) {
      const candidate = [...row.map((r) => r.area), remaining[i].area]
      const w = worst(candidate, shortSide)
      if (w > bestWorst) {
        // Adding this item makes the row worse; place current row, restart.
        break
      }
      bestWorst = w
      row.push(remaining[i])
    }

    if (row.length === 0) {
      // No item fit (defensive); take one to avoid infinite loop.
      row = [remaining[0]]
    }

    // Place row.
    const sum = row.reduce((s, r) => s + r.area, 0)
    if (sum <= 0) {
      remaining = remaining.slice(row.length)
      continue
    }

    if (current.w <= current.h) {
      // horizontal row across the top
      const h = sum / current.w
      let x = current.x
      for (const r of row) {
        const rw = r.area / h
        out.push({ ...r, x, y: current.y, w: rw, h })
        x += rw
      }
      current = { x: current.x, y: current.y + h, w: current.w, h: current.h - h }
    } else {
      // vertical row down the left
      const w = sum / current.h
      let y = current.y
      for (const r of row) {
        const rh = r.area / w
        out.push({ ...r, x: current.x, y, w, h: rh })
        y += rh
      }
      current = { x: current.x + w, y: current.y, w: current.w - w, h: current.h }
    }

    remaining = remaining.slice(row.length)
  }

  return out
}
